import type { IInvestmentRepository } from "@/application/ports/repositories";
import type {
  InvestmentCreateRequest,
  InvestmentUpdateRequest,
  InvestmentResponse,
  InvestmentWithDetailsResponse,
  InvestmentQueryParams,
  InvestmentExportParams,
  InvestmentPagination,
  ExtendReservationRequest,
  ExtendReservationResponse,
  SendReservationContractRequest,
  SendReservationContractResponse,
  SendFinancingContractRequest,
  SendFinancingContractResponse,
  SendCommunicationContractRequest,
  SendCommunicationContractResponse,
  RetryContractDispatchResponse,
  DownloadAeatDocumentsRequest,
} from "@/domain/investment";
import type {
  InvestmentDocumentResponse,
  DocumentDownloadResponse,
  PaginatedResponse,
  ReviewUpdateRequest,
  DocumentUpdateRequest,
} from "@/domain/documentBlocks";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import { downloadBlob } from "../fileDownload";
import { apiClient, ApiClientError } from "../client";
import { buildExportPreviewUrl, buildExportDownloadUrl } from "../exportUrls";
import { getLogger } from "@/infrastructure/di/services";

export class InvestmentRepository implements IInvestmentRepository {
  private basePath = "/investments";
  private validateResponse<T>(data: unknown, requiredKeys: string[]): T {
    if (!data || typeof data !== "object") {
      throw new ApiClientError({
        message: "Invalid response format",
        status: 500,
        code: "INVALID_RESPONSE",
        details: { received: data },
      });
    }
    const missingKeys = requiredKeys.filter((key) => !(key in (data as object)));
    if (missingKeys.length > 0) {
      throw new ApiClientError({
        message: "Response missing required fields",
        status: 500,
        code: "INCOMPLETE_RESPONSE",
        details: { missingKeys, received: data },
      });
    }
    return data as T;
  }

  private handleError(error: unknown, operation: string): never {
    getLogger().error(`Investment ${operation} failed:`, error);
    if (error instanceof ApiClientError) throw error;
    if (error instanceof Error) {
      throw new ApiClientError({
        message: error.message || `Failed to ${operation}`,
        status: 0,
        code: "NETWORK_ERROR",
        details: { originalError: error.message },
      });
    }
    throw new ApiClientError({
      message: `An unexpected error occurred during ${operation}`,
      status: 0,
      code: "UNKNOWN_ERROR",
      details: { error: String(error) },
    });
  }

  async getInvestments(params?: InvestmentQueryParams): Promise<InvestmentPagination> {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        if (params.state) {
          if (Array.isArray(params.state)) {
            params.state.forEach((s) => queryParams.append("state", s));
          } else {
            queryParams.append("state", params.state);
          }
        }
        if (params.investor_uuid) queryParams.append("investor_uuid", params.investor_uuid);
        if (params.beneficiary_uuid)
          queryParams.append("beneficiary_uuid", params.beneficiary_uuid);
        if (params.fiscal_quota_uuid)
          queryParams.append("fiscal_quota_uuid", params.fiscal_quota_uuid);
        if (params.search) queryParams.append("search", params.search);
        if (params.amount_min) queryParams.append("amount_min", params.amount_min);
        if (params.amount_max) queryParams.append("amount_max", params.amount_max);
        if (params.is_archived !== undefined)
          queryParams.append("is_archived", params.is_archived.toString());
        if (params.investor_type) queryParams.append("investor_type", params.investor_type);
        if (params.fiscal_year) queryParams.append("fiscal_year", params.fiscal_year);
        if (params.sort_by) queryParams.append("sort_by", params.sort_by);
        if (params.sort_direction) queryParams.append("sort_direction", params.sort_direction);
        if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());
        if (params.offset !== undefined) queryParams.append("offset", params.offset.toString());
      }

      const url = queryParams.toString()
        ? `${this.basePath}?${queryParams.toString()}`
        : this.basePath;

      const response = await apiClient.get<InvestmentPagination>(url);
      return this.validateResponse<InvestmentPagination>(response.data, [
        "investments",
        "total_count",
        "total_unfiltered_count",
        "limit",
        "offset",
      ]);
    } catch (error) {
      throw this.handleError(error, "fetch investments");
    }
  }

  async getInvestment(uuid: string): Promise<InvestmentWithDetailsResponse> {
    try {
      const response = await apiClient.get<InvestmentWithDetailsResponse>(
        `${this.basePath}/${uuid}`,
      );
      return this.validateResponse<InvestmentWithDetailsResponse>(response.data, [
        "investment",
        "events",
        "documents",
      ]);
    } catch (error) {
      throw this.handleError(error, `fetch investment ${uuid}`);
    }
  }

  async createInvestment(data: InvestmentCreateRequest): Promise<InvestmentResponse> {
    try {
      const response = await apiClient.post<InvestmentResponse>(this.basePath, data);
      return this.validateResponse<InvestmentResponse>(response.data, [
        "uuid",
        "amount",
        "return_percentage",
        "state",
        "investor",
        "beneficiary",
        "quota",
      ]);
    } catch (error) {
      throw this.handleError(error, "create investment");
    }
  }

  async updateInvestment(uuid: string, data: InvestmentUpdateRequest): Promise<InvestmentResponse> {
    try {
      const response = await apiClient.patch<InvestmentResponse>(`${this.basePath}/${uuid}`, data);
      return this.validateResponse<InvestmentResponse>(response.data, [
        "uuid",
        "amount",
        "return_percentage",
        "state",
        "investor",
        "beneficiary",
        "quota",
      ]);
    } catch (error) {
      throw this.handleError(error, `update investment ${uuid}`);
    }
  }

  async deleteInvestment(uuid: string): Promise<void> {
    try {
      await apiClient.delete(`${this.basePath}/${uuid}`);
    } catch (error) {
      throw this.handleError(error, `delete investment ${uuid}`);
    }
  }

  async extendReservation(
    uuid: string,
    data: ExtendReservationRequest,
  ): Promise<ExtendReservationResponse> {
    try {
      const response = await apiClient.post<ExtendReservationResponse>(
        `/investments/${uuid}/actions/extend-reservation`,
        data,
      );
      return this.validateResponse<ExtendReservationResponse>(response.data, [
        "extension_granted",
        "new_expires_at",
        "notification_sent",
        "extended_at",
      ]);
    } catch (error) {
      throw this.handleError(error, `extend reservation for investment ${uuid}`);
    }
  }

  async sendReservationContract(
    uuid: string,
    data: SendReservationContractRequest,
  ): Promise<SendReservationContractResponse> {
    try {
      // 202 Accepted — DocuSign runs in a background task on the server.
      // Short timeout is fine; we only wait for PDF render + GCS upload + DB write.
      const response = await apiClient.post<SendReservationContractResponse>(
        `/investments/${uuid}/actions/send-reservation-contract`,
        data,
        { timeout: 30000 },
      );
      return this.validateResponse<SendReservationContractResponse>(response.data, [
        "contract_dispatched",
        "document_created",
        "document_uuid",
        "signature_status",
      ]);
    } catch (error) {
      throw this.handleError(error, `send reservation contract for investment ${uuid}`);
    }
  }

  async sendFinancingContract(
    uuid: string,
    data: SendFinancingContractRequest,
  ): Promise<SendFinancingContractResponse> {
    try {
      const response = await apiClient.post<SendFinancingContractResponse>(
        `/investments/${uuid}/actions/send-financing-contract`,
        data,
        { timeout: 30000 },
      );
      return this.validateResponse<SendFinancingContractResponse>(response.data, [
        "contract_dispatched",
        "document_created",
        "document_uuid",
        "signature_status",
      ]);
    } catch (error) {
      throw this.handleError(error, `send financing contract for investment ${uuid}`);
    }
  }

  async previewReservationContract(
    uuid: string,
    data: SendReservationContractRequest,
  ): Promise<string> {
    try {
      const blob = await apiClient.postBlob(
        `/investments/${uuid}/actions/preview-reservation-contract`,
        data,
        { timeout: 15000 },
      );
      return URL.createObjectURL(blob);
    } catch (error) {
      throw this.handleError(error, `preview reservation contract for investment ${uuid}`);
    }
  }

  async previewFinancingContract(
    uuid: string,
    data: SendFinancingContractRequest,
  ): Promise<string> {
    try {
      const blob = await apiClient.postBlob(
        `/investments/${uuid}/actions/preview-financing-contract`,
        data,
        { timeout: 15000 },
      );
      return URL.createObjectURL(blob);
    } catch (error) {
      throw this.handleError(error, `preview financing contract for investment ${uuid}`);
    }
  }

  async sendCommunicationContract(
    uuid: string,
    data: SendCommunicationContractRequest,
  ): Promise<SendCommunicationContractResponse> {
    try {
      const response = await apiClient.post<SendCommunicationContractResponse>(
        `/investments/${uuid}/actions/send-communication-contract`,
        data,
        { timeout: 60000 },
      );
      return this.validateResponse<SendCommunicationContractResponse>(response.data, [
        "contract_dispatched",
        "document_created",
        "document_uuid",
        "signature_status",
      ]);
    } catch (error) {
      throw this.handleError(error, `send communication contract for investment ${uuid}`);
    }
  }

  async previewCommunicationContract(
    uuid: string,
    data: SendCommunicationContractRequest,
  ): Promise<string> {
    try {
      const blob = await apiClient.postBlob(
        `/investments/${uuid}/actions/preview-communication-contract`,
        data,
        { timeout: 30000 },
      );
      return URL.createObjectURL(blob);
    } catch (error) {
      throw this.handleError(error, `preview communication contract for investment ${uuid}`);
    }
  }

  async retryContractDispatch(
    investmentUuid: string,
    documentUuid: string,
  ): Promise<RetryContractDispatchResponse> {
    try {
      const response = await apiClient.post<RetryContractDispatchResponse>(
        `/investments/${investmentUuid}/documents/${documentUuid}/actions/retry-dispatch`,
        {},
        { timeout: 15000 },
      );
      return this.validateResponse<RetryContractDispatchResponse>(response.data, [
        "contract_dispatched",
        "document_uuid",
        "signature_status",
      ]);
    } catch (error) {
      throw this.handleError(error, `retry contract dispatch for document ${documentUuid}`);
    }
  }

  async listDocuments(
    investmentUuid: string,
    params?: {
      document_type?: string;
      status?: string;
      search?: string;
      limit?: number;
      offset?: number;
    },
  ): Promise<PaginatedResponse<InvestmentDocumentResponse>> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.document_type) searchParams.append("document_type", params.document_type);
      if (params?.status) searchParams.append("status", params.status);
      if (params?.search) searchParams.append("search", params.search);
      if (params?.limit) searchParams.append("limit", params.limit.toString());
      if (params?.offset) searchParams.append("offset", params.offset.toString());

      const query = searchParams.toString();
      const url = query
        ? `${this.basePath}/${investmentUuid}/documents?${query}`
        : `${this.basePath}/${investmentUuid}/documents`;
      const response = await apiClient.get<PaginatedResponse<InvestmentDocumentResponse>>(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error, `list documents for investment ${investmentUuid}`);
    }
  }

  async uploadDocument(
    uuid: string,
    file: File,
    documentType: string,
    title?: string,
  ): Promise<InvestmentDocumentResponse> {
    try {
      const formData = new FormData();
      formData.append("document", file);

      let queryParams = `document_type=${documentType}`;
      if (title) {
        queryParams += `&title=${encodeURIComponent(title)}`;
      }

      const response = await apiClient.upload<InvestmentDocumentResponse>(
        `/investments/${uuid}/documents?${queryParams}`,
        formData,
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, `upload document for investment ${uuid}`);
    }
  }

  async downloadDocument(investmentUuid: string, documentUuid: string): Promise<void> {
    try {
      const docInfo = await this.getDocumentDownloadUrl(investmentUuid, documentUuid);

      const response = await fetch(docInfo.signed_url, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        headers: { Accept: "application/pdf,image/*" },
      });

      if (!response.ok) {
        throw new ApiClientError({
          message: `Failed to download document: ${response.status} ${response.statusText}`,
          code: "DOWNLOAD_ERROR",
          status: response.status,
        });
      }

      const blob = await response.blob();
      downloadBlob(blob, docInfo.filename || `investment-document-${documentUuid}.pdf`);
    } catch (error) {
      throw this.handleError(
        error,
        `download document ${documentUuid} from investment ${investmentUuid}`,
      );
    }
  }

  async reviewDocument(
    investmentUuid: string,
    documentUuid: string,
    review: ReviewUpdateRequest,
  ): Promise<InvestmentDocumentResponse> {
    try {
      const response = await apiClient.patch<InvestmentDocumentResponse>(
        `/investments/${investmentUuid}/documents/${documentUuid}/review`,
        review,
      );
      return response.data;
    } catch (error) {
      throw this.handleError(
        error,
        `review document ${documentUuid} for investment ${investmentUuid}`,
      );
    }
  }

  async updateDocument(
    investmentUuid: string,
    documentUuid: string,
    data: DocumentUpdateRequest,
  ): Promise<InvestmentDocumentResponse> {
    try {
      const response = await apiClient.patch<InvestmentDocumentResponse>(
        `/investments/${investmentUuid}/documents/${documentUuid}`,
        data,
      );
      return response.data;
    } catch (error) {
      throw this.handleError(
        error,
        `update document ${documentUuid} for investment ${investmentUuid}`,
      );
    }
  }

  async deleteDocument(investmentUuid: string, documentUuid: string): Promise<void> {
    try {
      await apiClient.delete(`/investments/${investmentUuid}/documents/${documentUuid}`);
    } catch (error) {
      throw this.handleError(
        error,
        `delete document ${documentUuid} from investment ${investmentUuid}`,
      );
    }
  }

  async getDocumentDownloadUrl(
    investmentUuid: string,
    documentUuid: string,
  ): Promise<DocumentDownloadResponse> {
    try {
      const response = await apiClient.get<DocumentDownloadResponse>(
        `/investments/${investmentUuid}/documents/${documentUuid}/download`,
      );
      return response.data;
    } catch (error) {
      throw this.handleError(
        error,
        `get document ${documentUuid} from investment ${investmentUuid}`,
      );
    }
  }

  async voidDocument(
    investmentUuid: string,
    documentUuid: string,
  ): Promise<InvestmentDocumentResponse> {
    try {
      const response = await apiClient.patch<InvestmentDocumentResponse>(
        `/investments/${investmentUuid}/documents/${documentUuid}/void`,
      );
      return response.data;
    } catch (error) {
      throw this.handleError(
        error,
        `void document ${documentUuid} from investment ${investmentUuid}`,
      );
    }
  }

  async previewExport(params: InvestmentExportParams): Promise<ExportPreview> {
    const url = buildExportPreviewUrl(this.basePath, investmentExportParamsToQuery(params));
    const response = await apiClient.get<ExportPreview>(url);
    return response.data;
  }

  async downloadExport(
    params: InvestmentExportParams,
    options: ExportDownloadOptions,
  ): Promise<void> {
    const url = buildExportDownloadUrl(
      this.basePath,
      investmentExportParamsToQuery(params),
      options,
    );
    const { blob, filename } = await apiClient.getBlobWithFilename(
      url,
      `investments.${options.format}`,
    );
    downloadBlob(blob, filename);
  }

  async downloadAeatDocuments(uuid: string, data: DownloadAeatDocumentsRequest): Promise<void> {
    try {
      const { blob, filename } = await apiClient.postBlobWithFilename(
        `/investments/${uuid}/actions/download-aeat`,
        data,
        `aeat_documents_${uuid}.zip`,
        {
          timeout: 30000,
          headers: { Accept: "application/zip" },
        },
      );
      downloadBlob(blob, filename);
    } catch (error) {
      throw this.handleError(error, `download AEAT documents for investment ${uuid}`);
    }
  }
}

function investmentExportParamsToQuery(params: InvestmentExportParams): URLSearchParams {
  const query = new URLSearchParams();
  if (params.state) query.append("state", params.state);
  if (params.investor_uuid) query.append("investor_uuid", params.investor_uuid);
  if (params.beneficiary_uuid) query.append("beneficiary_uuid", params.beneficiary_uuid);
  if (params.fiscal_quota_uuid) query.append("fiscal_quota_uuid", params.fiscal_quota_uuid);
  if (params.search) query.append("search", params.search);
  if (params.amount_min) query.append("amount_min", params.amount_min);
  if (params.amount_max) query.append("amount_max", params.amount_max);
  if (params.is_archived !== undefined) query.append("is_archived", String(params.is_archived));
  if (params.investor_type) query.append("investor_type", params.investor_type);
  if (params.fiscal_year) query.append("fiscal_year", params.fiscal_year);
  if (params.sort_by) query.append("sort_by", params.sort_by);
  if (params.sort_direction) query.append("sort_direction", params.sort_direction);
  return query;
}
